import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { FieldError, Input, Label } from './input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      'flex min-h-20 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive',
      className,
    )}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

/** Accessible checkbox-based switch; dense, no extra dependency. */
export const Switch = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: ReactNode }>(
  ({ className, label, id, ...props }, ref) => {
    const auto = useId();
    const inputId = id ?? auto;
    return (
      <label htmlFor={inputId} className={cn('inline-flex cursor-pointer items-center gap-2 text-sm', className)}>
        <input ref={ref} id={inputId} type="checkbox" role="switch" className="peer sr-only" {...props} />
        <span
          aria-hidden
          className="relative h-4 w-7 rounded-full bg-muted transition-colors after:absolute after:left-0.5 after:top-0.5 after:size-3 after:rounded-full after:bg-background after:shadow after:transition-transform peer-checked:bg-accent peer-checked:after:translate-x-3 peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-disabled:opacity-50"
        />
        {label && <span>{label}</span>}
      </label>
    );
  },
);
Switch.displayName = 'Switch';

/** Label + control + error, stacked. */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      <FieldError message={error} />
    </div>
  );
}

export interface SecretInputProps {
  id?: string;
  /** True when the server holds a value (shown as ****, never returned). */
  configured: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
}

/**
 * Write-only secret field (11 §2): never displays a stored value. When a secret is configured it shows `****`
 * and a "Replace" button; an empty value means "keep the stored secret" and callers must omit it from the request.
 */
export function SecretInput({ id, configured, value, onChange, placeholder, invalid, disabled }: SecretInputProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(!configured);

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <Input id={id} value="****" readOnly aria-label={t('admin.secret.stored')} className="font-mono" disabled />
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)} disabled={disabled}>
          {t('admin.secret.replace')}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        type="password"
        autoComplete="new-password"
        value={value}
        placeholder={placeholder ?? (configured ? t('admin.secret.keepHint') : '')}
        aria-invalid={invalid}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {configured && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            onChange('');
            setEditing(false);
          }}
        >
          {t('admin.secret.cancel')}
        </Button>
      )}
    </div>
  );
}

/** Returns the secret to send, or undefined to keep the stored one. */
export function secretToSend(value: string): string | undefined {
  const v = value.trim();
  return v === '' ? undefined : v;
}

export interface TabItem {
  id: string;
  label: ReactNode;
  hidden?: boolean;
}

/** Simple accessible tab strip (content rendered by the caller based on `value`). */
export function Tabs({ items, value, onChange }: { items: TabItem[]; value: string; onChange: (id: string) => void }) {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b px-6">
      {items
        .filter((i) => !i.hidden)
        .map((i) => (
          <button
            key={i.id}
            role="tab"
            type="button"
            aria-selected={value === i.id}
            onClick={() => onChange(i.id)}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-2.5 py-2 text-sm transition-colors',
              value === i.id ? 'border-accent font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {i.label}
          </button>
        ))}
    </div>
  );
}

/** Dense table wrapper with consistent cell styling. */
export function DataTable({ head, children, empty }: { head: ReactNode[]; children: ReactNode; empty?: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="px-3 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y [&_td]:px-3 [&_td]:py-2 [&_td]:align-top">{children}</tbody>
      </table>
      {empty}
    </div>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-6 text-center text-sm text-muted-foreground">
        {children}
      </td>
    </tr>
  );
}

export function RootError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
      {message}
    </p>
  );
}

export function Section({ title, description, actions, children }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
